import { FC, MouseEvent } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { GoToOrgIcon, makeSxStyles } from "ui";
import { AdminOrganizationResDto } from "service/api/estabrookSchemas";
import { AdRoutes } from "@/constants/ad-routes";
import { ORGANIZATIONS_LIST_COLUMNS } from "../constants/organizations-list-columns";
import OrganizationAccessToggle from "./OrganizationAccessToggle";
import OrganizationActionsMenu from "./OrganizationActionsMenu";
import useImpersonation from "../hooks/useImpersonation";

export interface OrganizationCardProps {
  organization: AdminOrganizationResDto;
  onToggleAccessRequest: (organization: AdminOrganizationResDto) => void;
  onEditRequest: (organization: AdminOrganizationResDto) => void;
  onDeleteRequest: (organization: AdminOrganizationResDto) => void;
}

const ellipsisSx = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
};

const formatDate = (value: string | null | undefined): string => {
  return value ? DateTime.fromISO(value).toFormat("dd MMMM yyyy") : "-";
};

const formatLocation = (city: string | null, state: string): string | null => {
  if (city && state) return `${city}, ${state}`;
  return city || state || null;
};

const OrganizationCard: FC<OrganizationCardProps> = ({
  organization,
  onToggleAccessRequest,
  onEditRequest,
  onDeleteRequest,
}) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { start, isStarting } = useImpersonation();

  const stopPropagation = (event: MouseEvent) => event.stopPropagation();

  const goToOrganizationDetail = () => {
    navigate(
      AdRoutes.organizationDetail.replace(":organizationId", organization.id),
    );
  };

  const location = formatLocation(organization.city, organization.state);
  const [
    nameColumn,
    locationColumn,
    accessColumn,
    createdColumn,
    updatedColumn,
    goToOrgColumn,
    actionsColumn,
  ] = ORGANIZATIONS_LIST_COLUMNS;

  return (
    <Box
      sx={styles.card}
      onClick={goToOrganizationDetail}
      data-testid={`organization-card-${organization.id}`}
    >
      <Box
        sx={{
          ...styles.cell,
          width: nameColumn.size,
          minWidth: nameColumn.minWidth,
        }}
      >
        <Tooltip title={organization.name}>
          <Typography sx={{ ...styles.text, ...ellipsisSx }}>
            {organization.name}
          </Typography>
        </Tooltip>
      </Box>
      <Box
        sx={{
          ...styles.cell,
          width: locationColumn.size,
          minWidth: locationColumn.minWidth,
        }}
      >
        <Tooltip title={location || ""}>
          <Typography sx={{ ...styles.text, ...ellipsisSx }}>
            {location || "-"}
          </Typography>
        </Tooltip>
      </Box>
      <Box
        sx={{
          ...styles.cell,
          width: accessColumn.size,
          minWidth: accessColumn.minWidth,
          justifyContent: "center",
        }}
        onClick={stopPropagation}
      >
        <OrganizationAccessToggle
          checked={organization.isActive}
          onChange={() => onToggleAccessRequest(organization)}
          data-testid={`organization-active-toggle-${organization.id}`}
        />
      </Box>
      <Box
        sx={{
          ...styles.cell,
          width: createdColumn.size,
          minWidth: createdColumn.minWidth,
        }}
      >
        <Typography noWrap sx={styles.text}>
          {formatDate(organization.createdAt)}
        </Typography>
      </Box>
      <Box
        sx={{
          ...styles.cell,
          width: updatedColumn.size,
          minWidth: updatedColumn.minWidth,
        }}
      >
        <Typography noWrap sx={styles.text}>
          {formatDate(organization.updatedAt)}
        </Typography>
      </Box>
      <Box
        sx={{
          ...styles.cell,
          width: goToOrgColumn.size,
          minWidth: goToOrgColumn.minWidth,
          justifyContent: "center",
        }}
        onClick={stopPropagation}
      >
        <Tooltip title="Go to organization">
          <span>
            <IconButton
              disabled={isStarting}
              onClick={() => start(organization.id)}
              sx={styles.iconButton}
              data-testid="go-to-org"
            >
              <GoToOrgIcon width={15} height={15} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box
        sx={{
          ...styles.cell,
          width: actionsColumn.size,
          minWidth: actionsColumn.minWidth,
          justifyContent: "flex-end",
        }}
        onClick={stopPropagation}
      >
        <OrganizationActionsMenu
          onEdit={() => onEditRequest(organization)}
          onDelete={() => onDeleteRequest(organization)}
        />
      </Box>
    </Box>
  );
};

const useStyles = makeSxStyles(({ palette: { grey } }) => ({
  card: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    border: `1px solid ${grey[400]}`,
    borderRadius: "12px",
    cursor: "pointer",
    "&:hover": { background: grey[600] },
  },
  cell: {
    display: "flex",
    alignItems: "center",
    height: "56px",
    padding: "16px",
    boxSizing: "border-box",
  },
  text: {
    color: grey[100],
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.1px",
  },
  iconButton: {
    width: "32px",
    height: "32px",
    padding: 0,
  },
}));

export default OrganizationCard;
